// ═══════════════════════════════════════════════════════════════
// PolicyChat — Floating AI policy assistant
// Answers questions from ingested FEMA/EM policy documents
// with source citations.
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useCallback } from 'react';
import { IconBook2, IconX, IconSend, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import './PolicyChat.css';

interface Source {
  doc:       string;
  pageLabel: string;
  excerpt:   string;
}

interface Message {
  role:    'user' | 'assistant';
  text:    string;
  sources?: Source[];
}

const SUGGESTIONS = [
  'What qualifies for FEMA Public Assistance?',
  'How does the HMA grant application work?',
  'What is the IHP program and who qualifies?',
  'What are the deadlines for PA project submissions?',
];

export default function PolicyChat() {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef       = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  const send = useCallback(async (question: string) => {
    const q = question.trim();
    if (!q || loading) return;

    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setInput('');
    setLoading(true);

    try {
      const res  = await fetch('/api/policy-chat', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ question: q }),
      });
      const data = await res.json();

      setMessages(prev => [...prev, {
        role:    'assistant',
        text:    data.answer || data.error || 'Something went wrong.',
        sources: data.sources || [],
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Network error — please try again.',
      }]);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const toggleSource = (msgIdx: number) => {
    setExpanded(prev => ({ ...prev, [msgIdx]: !prev[msgIdx] }));
  };

  return (
    <>
      {/* ── Floating bubble ── */}
      <button
        className="pchat-bubble"
        onClick={() => setOpen(o => !o)}
        title="Policy Assistant"
        aria-label="Open policy chat"
      >
        {open ? <IconX size={22} /> : <IconBook2 size={22} />}
      </button>

      {/* ── Chat panel ── */}
      {open && (
        <div className="pchat-panel">
          {/* Header */}
          <div className="pchat-header">
            <div>
              <div className="pchat-header-title">
                <IconBook2 size={16} />
                Policy Assistant
              </div>
              <div className="pchat-header-sub">FEMA PAPPG · HMA · IHP · Local Officials Guide</div>
            </div>
            <button className="pchat-close" onClick={() => setOpen(false)} aria-label="Close">
              <IconX size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="pchat-messages">
            {messages.length === 0 && !loading ? (
              <div className="pchat-empty">
                <div className="pchat-empty-icon">📋</div>
                <div className="pchat-empty-title">Ask a policy question</div>
                <div className="pchat-empty-sub">
                  I'll answer from your FEMA and EM policy documents and show you exactly where the answer came from.
                </div>
                <div className="pchat-suggestions">
                  {SUGGESTIONS.map(s => (
                    <button key={s} className="pchat-chip" onClick={() => send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} className={`pchat-msg pchat-msg-${msg.role}`}>
                  <div className="pchat-bubble-text">{msg.text}</div>

                  {/* Source citations — only on assistant messages */}
                  {msg.role === 'assistant' && msg.sources && msg.sources.length > 0 && (
                    <div className="pchat-sources">
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#888', display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0', fontFamily: 'inherit' }}
                        onClick={() => toggleSource(i)}
                      >
                        {expanded[i] ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
                        {msg.sources.length} source{msg.sources.length !== 1 ? 's' : ''}
                      </button>
                      {expanded[i] && msg.sources.map((src, si) => (
                        <div key={si} className="pchat-source">
                          <div className="pchat-source-doc">
                            {src.doc}{src.pageLabel ? ` — ${src.pageLabel}` : ''}
                          </div>
                          <div className="pchat-source-excerpt">{src.excerpt}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Typing indicator */}
            {loading && (
              <div className="pchat-msg pchat-msg-assistant">
                <div className="pchat-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input bar */}
          <div className="pchat-input-bar">
            <textarea
              ref={inputRef}
              className="pchat-input"
              placeholder="Ask a policy question…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={loading}
            />
            <button
              className="pchat-send"
              onClick={() => send(input)}
              disabled={!input.trim() || loading}
              aria-label="Send"
            >
              <IconSend size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
